import { useScrollReveal } from "@/hooks/useScrollReveal";

interface CriticalAction {
  label: string;
  icon: string;
  count: number;
  onClick?: () => void;
}

interface CriticalActionBannerProps {
  greeting: string;
  userName: string;
  actions: CriticalAction[];
  onResolveAll?: () => void;
  animate?: boolean;
}

export default function CriticalActionBanner({
  greeting, userName, actions, onResolveAll, animate = true,
}: CriticalActionBannerProps) {
  const ref = useScrollReveal<HTMLDivElement>({
    selector: ".action-card",
    staggerDelay: 100,
    threshold: 0.15,
  });

  if (actions.length === 0) return null;

  const totalActions = actions.reduce((sum, a) => sum + a.count, 0);

  return (
    <div className={`relative overflow-hidden rounded-3xl p-6 md:p-7 bg-gradient-to-br from-foreground-800 to-foreground-900 text-white shadow-soft-lg ${animate ? "hero-reveal" : ""}`}>
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-white/3 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-red-500/8 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />
      <div className="absolute top-1/2 right-1/3 w-40 h-40 bg-amber-400/5 rounded-full blur-3xl" />

      <div className="relative z-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-12 h-12 rounded-2xl bg-red-500/20 border border-red-400/20 flex-shrink-0">
              <i className="ri-shield-flash-line text-red-300 text-xl" />
            </span>
            <div>
              <p className="text-2xl md:text-3xl font-bold tracking-tight">
                {greeting}, {userName}
              </p>
              <p className="text-sm text-foreground-300 mt-0.5">
                Hoy debes resolver <span className="text-red-400 font-bold">{totalActions} acción{totalActions !== 1 ? "es" : ""} crítica{totalActions !== 1 ? "s" : ""}</span>
              </p>
            </div>
          </div>
          {onResolveAll && (
            <button
              onClick={onResolveAll}
              className="sm:ml-auto flex-shrink-0 flex items-center gap-2 px-5 py-3 bg-red-500/90 hover:bg-red-500 backdrop-blur border border-red-400/30 text-white rounded-xl text-sm font-bold hover:shadow-glow-red transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <span className="flex items-center justify-center w-4 h-4">
                <i className="ri-flashlight-line" />
              </span>
              Resolver ahora
            </button>
          )}
        </div>

        {/* Action cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" ref={animate ? ref : undefined}>
          {actions.map((action, idx) => (
            <button
              key={idx}
              onClick={action.onClick}
              className={`action-card flex items-start gap-3 p-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all duration-200 cursor-pointer text-left group ${animate ? "scroll-reveal" : ""}`}
              style={animate ? { transitionDelay: `${idx * 100}ms` } : undefined}
            >
              <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/10 flex-shrink-0 mt-0.5 group-hover:bg-white/15 transition-colors">
                <i className={`${action.icon} text-white/80 text-base`} />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate">{action.label}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
                  <span className="text-xs text-white/60">{action.count} pendiente{action.count !== 1 ? "s" : ""}</span>
                </div>
              </div>
              <span className="flex items-center justify-center w-4 h-4 text-white/40 group-hover:translate-x-0.5 transition-transform flex-shrink-0 mt-1.5">
                <i className="ri-arrow-right-s-line" />
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}